import { parallel, parallelLimit } from 'async';
import { uniq, each, reduce, chunk, concat } from 'lodash';
import callApi from '../../util/apiCaller';

export const LOAD_DYNAMIC_DATA = 'LOAD_DYNAMIC_DATA';
export const CLEAR_DYNAMIC_DATA = 'CLEAR_DYNAMIC_DATA';

function getResourceType(type) {
  switch (type) {
    case 'OPPORTUNITY': {
      return 'opportunities';
    }
    case 'ACCOUNT': {
      return 'accounts';
    }
    case 'OWNER': {
      return 'owners';
    }
    case 'PRODUCT': {
      return 'products';
    }
    case 'COMPETITOR': {
      return 'competitors';
    }
    default:
      return null;
  }
}

function loadObjectsFromApi(ids, type) {
  // TODO:
  const resourceType = getResourceType(type);
  if (resourceType) {
    // id length is about 20 chars
    const chunkSize = 50; // in the query, so that it not excceed url limit
    const maxTasks = 10; // total tasks running at the same time

    const getWithIds = chunkIds => new Promise((resolve, reject) => {
      callApi(`details/${resourceType}`, 'GET', { ids: chunkIds.join(',') })
        .then(
          ({ items }) => {
            resolve(items);
          },
          (error) => {
            reject(error);
          },
        );
    });
    return new Promise((resolve, reject) => {
      const chunks = chunk(ids, chunkSize);
      const tasks = chunks.map(chunkIds => (cb) => {
        if (chunkIds.length) {
          getWithIds(chunkIds)
            .then(
              (records) => {
                cb(null, records);
              },
              (error) => {
                cb(chunkIds.map(id => ({
                  error,
                  id,
                })));
              },
            );
        } else {
          cb(null, []);
        }
      });

      parallelLimit(
        tasks,
        maxTasks,
        (error, results) => {
          if (error) {
            reject(concat(...error));
          } else {
            // resolve(results);
            resolve(concat(...results));
          }
        },
      );
    });
  }
  return Promise.reject(new Error(`Invalid Resource type: ${type}`));
}


export function memoize(wait = 1000) {
  let cache = {};
  let timeout = null;
  let queueIds = {};

  const clean = (dispatch) => {
    each(cache, (ids, resourceType) => {
      dispatch({
        type: CLEAR_DYNAMIC_DATA,
        resourceType,
      });
    });
    cache = {};
  };

  const request = (id, type, dispatch) => {
    if (!cache[type]) {
      cache[type] = [];
    }
    if (!queueIds[type]) {
      queueIds[type] = [];
    }

    if (!cache[type][id]) {
      cache[type][id] = {
        status: 'loading',
        data: null,
        error: null,
      };
      queueIds[type] = uniq(queueIds[type].concat(id));
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const tasks = reduce(queueIds, (items, ids, resourceType) => {
          dispatch({
            type: LOAD_DYNAMIC_DATA,
            resourceType,
            results: reduce(ids, (r, i) => {
              if (cache[type] && cache[type][i] && cache[type][i].status === 'loading') {
                r.push({
                  data: null,
                  error: null,
                  id: i,
                  status: 'loading',
                });
              }
              return r;
            }, []),
          });
          items[resourceType] = (cb) => {
            loadObjectsFromApi(ids, resourceType)
              .then(
                (results) => {
                  cb(null, results.map(result => ({
                    status: 'loaded',
                    properties: result.properties,
                    id: result.id,
                  })));
                },
                (results) => {
                  cb(null, results.map(result => ({
                    status: 'loaded',
                    error: result.error,
                    id: result.id,
                  })));
                },
              );
          };
          return items;
        }, {});
        queueIds = {};

        parallel(tasks, (errors, objects) => {
          if (!errors) {
            each(objects, (results, resourceType) => {
              each(results, (result) => {
                if (result.error) {
                  cache[resourceType][result.id].error = result.error;
                  cache[resourceType][result.id].status = 'error';
                } else {
                  cache[resourceType][result.id].data = result.properties;
                  cache[resourceType][result.id].status = 'loaded';
                }
              });
              dispatch({
                type: LOAD_DYNAMIC_DATA,
                resourceType,
                results: reduce(results, (items, result) => items.concat({
                  data: result.error ? null : result.properties,
                  error: result.error ? result.error : null,
                  id: result.id,
                  status: result.error ? 'error' : 'loaded',
                }), []),
              });
            });
          }
        });
      }, wait);
    }
    return {
      data: cache[type][id].data,
      error: cache[type][id].error,
      status: cache[type][id].status,
    };
  };

  return {
    request,
    clean,
  };
}
const { request, clean } = memoize(1000);
export const loadObject = request;
export const clearCache = clean;
